import React, { useState, useEffect } from "react";
import {
  Cpu,
  Gauge,
  Droplets,
  Activity,
  Thermometer,
  Clock,
} from "lucide-react";
import { getStateDescriptions } from "../../utils/things/PRECISION";
import axios from "../../utils/axiosInstance";

const DataRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    {icon && <span className="flex-shrink-0">{icon}</span>}
    <span className="text-sm text-slate-400 flex-shrink-0">{label}</span>
    <span className="text-sm font-bold text-white ml-auto">{value}</span>
  </div>
);

export default function CompressorData({ data }) {
  const [mockData] = useState({
    unit_sd_enum: "Normal Operation",
    response: {
      FIELD_PRES: 0,
      SUCTION_PRES: 0,
      INTERSTAGE_PRES: 0,
      DISCHARGE_PRES: 0,
      COMP_OIL_PRES: 0,
      COMP_HOURMETER: 0,
      FLOW_METER: 0,
      DISCHARGE_TEMP_FINAL: 0,
      DISCHARGE_2_TEMP: 0,
      TAILSHAFT_TEMP: 0,
    },
  });

  const currentData = data || mockData;
  const [areaData, setAreaData] = useState(null);
  const [flag, setFlag] = useState(-100);
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/area-status`
        );
        const areas = response.data.data.map((area) => ({
          id: area.id,
          name: area.name,
          time: area.time,
          regionroute: area.regionroute,
          CtrlType: area.CtrlType === "WellPro II" ? "WP" : area.CtrlType,
          SDcode: area.SDcode,
          SDcodeDesc: area.SDcodeDesc,
          state: area.state,
          stateDesc: area.stateDesc,
          status: area.status,
          regionName: area.regionName,
          routeName: area.routeName,
        }));

        const currentArea = areas.find((a) => a.id === data?.unitId);
        if (currentArea) setAreaData(currentArea);
      } catch (err) {
        console.error("Error fetching areas:", err);
      }
    };

    if (data?.unitId) fetchAreas();
  }, [data?.unitId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!data?.unitId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/device-registers/${encodeURIComponent(data.unitId)}/1`
        );
        const firstRegister = response.data?.response?.[0];
        if (
          firstRegister &&
          data.time != null &&
          Number(data.time) <= Number(firstRegister.time) &&
          firstRegister.type === "MODBUS"
        ) {
          setFlag(-1);
        }
      } catch (err) {
        console.error("Error fetching compressorData:", {
          deviceId: data.unitId,
          error: err.response?.data || err.message,
          status: err.response?.status,
        });
      }
    };
    fetchData();
  }, [data?.unitId, data?.time]);

  const headerBg =
    areaData?.state != 12 && areaData?.state != 130 && areaData?.SDcode > 0
      ? "bg-gradient-to-l from-yellow-500 to-green-800 animate-pulse"
      : areaData?.state != 12 && areaData?.state != 130 && areaData?.SDcode == 0
      ? "bg-green-800"
      : areaData?.state === -1
      ? "bg-orange-500"
      : (areaData?.state == 12 || areaData?.state == 130) && areaData?.SDcode == 0
      ? "bg-gradient-to-r from-red-800 to-yellow-500 animate-pulse"
      : "bg-red-800";

  return (
    <div className="flex-1">
      {/* ✅ Temperature Warning Banner */}

      {/* --- Top Status Bar --- */}
      {flag === -1 ? (
        <div className="flex items-center p-3 bg-orange-500">
          <h3 className="text-sm font-semibold text-white">MODBUS ERROR</h3>
        </div>
      ) : (
        <div className={`flex items-center p-3 ${headerBg}`}>
          <h3 className="text-sm font-semibold text-white">
            {areaData?.SDcodeDesc || "Loading Compressor Status ..."}
          </h3>
        </div>
      )}

      {/* --- Data Section --- */}
      <div className="bg-slate-800 p-3">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700">
          <Cpu className="w-5 h-5 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">Compressor Data</h3>
        </div>

        <div className="space-y-1.5">
          <DataRow
            icon={<Gauge className="w-4 h-4 text-blue-400" />}
            label="Field Pressure"
            value={`${currentData.response.FIELD_PRES || 0} PSI`}
          />
          <DataRow
            icon={<Gauge className="w-4 h-4 text-cyan-400" />}
            label="Suction Pressure"
            value={`${currentData.response.SUCTION_PRES || 0} PSI`}
          />
          <DataRow
            icon={<Gauge className="w-4 h-4 text-purple-400" />}
            label="Interstage Pressure"
            value={`${currentData.response.INTERSTAGE_PRES || 0} PSI`}
          />
          <DataRow
            icon={<Gauge className="w-4 h-4 text-orange-400" />}
            label="Discharge Pressure"
            value={`${currentData.response.DISCHARGE_PRES || 0} PSI`}
          />
          <DataRow
            icon={<Droplets className="w-4 h-4 text-amber-400" />}
            label="Compressor Oil Press"
            value={`${currentData.response.COMP_OIL_PRES || 0} PSI`}
          />
          <DataRow
            icon={<Clock className="w-4 h-4 text-gray-400" />}
            label="Compressor Hours"
            value={`${currentData.response.COMP_HOURMETER / 10 || 0} h`}
          />
          <DataRow
            icon={<Activity className="w-4 h-4 text-green-400" />}
            label="Main Flow Rate"
            value={`${currentData.response.FLOW_METER || 0} SCFM`}
          />
          <DataRow
            icon={<Thermometer className="w-4 h-4 text-purple-400" />}
            label="1st Stage Discharge Temp"
            value={`${currentData.response.DISCHARGE_TEMP_FINAL || 0} °F`}
          />
          <DataRow
            icon={<Thermometer className="w-4 h-4 text-yellow-400" />}
            label="2nd Stage Discharge Temp"
            value={`${currentData.response.DISCHARGE_2_TEMP || 0} °F`}
          />

          {(currentData?.response?.CTRL_TYPE === "C5" ||
            currentData?.response?.R42487 === 1 ||
            currentData?.response?.R42488 === 1 ||
            currentData?.response?.R42489 === 1 ||
            currentData?.response?.R42490 === 1) &&
            currentData?.response?.CTRL_TYPE !== "C5E" && (
              <DataRow
                icon={<Thermometer className="w-4 h-4 text-green-400" />}
                label="Tailshaft Temp"
                value={`${currentData?.response?.TAILSHAFT_TEMP ?? 0} °F`}
              />
            )}
        </div>
      </div>
    </div>
  );
}
